import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, makeStateKey, OnDestroy, OnInit, signal, TransferState, WritableSignal } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { first, Subscription, firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { PublicidadService } from "src/app/servicios/publicidad/publicidad.service";
import { TitleWrapperService } from "src/app/servicios/title-wrapper/title-wrapper.service";
import { KebabCasePipe } from "../../pipes/kebab-case.pipe";
import { SkeletonImageComponent } from "../skeleton-image/skeleton-image/skeleton-image.component";
import { DetalleNoticiasService } from "@src/app/servicios/detalle-noticias/detalle-noticias.service";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";

const POST_KEY = makeStateKey<any>("detalle-post");

@Component({
  selector: "app-detalle-post",
  templateUrl: "./detalle-post.component.html",
  styleUrls: ["./detalle-post.component.css"],
  imports: [RouterLink, KebabCasePipe, CommonModule, SkeletonImageComponent],
  providers: [DetalleNoticiasService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetallePostComponent implements OnInit, OnDestroy {
  constantesGlobales: any;
  post: WritableSignal<any> = signal([]);
  rSub: Subscription;

  constructor(
    private readonly configService: ConfigService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly detalleNoticiasService: DetalleNoticiasService,
    private readonly publiService: PublicidadService,
    private readonly platformService: PlatformService,
    private readonly titleWrapperService: TitleWrapperService,
    private readonly transferState: TransferState,
    private readonly canonicalService: CanonicalService,
  ) {
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  ngOnInit(): void {
    this.loadPostById();
  }

  ngOnDestroy(): void {
    if (this.rSub) this.rSub.unsubscribe();
    this.transferState.remove(POST_KEY);
  }

  /**
   * Carga una publicación de pito 'Post' según el ID o slug proporcionado en los parámetros de la ruta.
   */
  loadPostById(): void {
    this.rSub = this.activatedRoute.params.subscribe((parms: any) => {
      if (isNaN(parms.id)) {
        this.getPost(parms.slug2, "slug");
      } else {
        this.getPost(parms.id);
      }
    });
  }

  /**
   * Recupera una publicación de tipo 'Post' del servidor o del estado de transferencia del SSR si está
   * disponible según los criterios de búsqueda y la clave de almacenamiento.
   * @param search - Criterios de búsqueda para recuperar una publicación. Podría ser un id o un slug,
   * dependiendo del parámetro `by`.
   * @param by - Criterios por los que buscar. De forma predeterminada, está configurado para buscar
   * por ID de publicación.
   */
  getPost(search, by = "id"): void {
    if (!this.platformService.isBrowser()) {
      this.fetchPostAndStoreKey(search, by);
    } else {
      if (this.transferState.hasKey(POST_KEY)) {
        const post = this.transferState.get<any>(POST_KEY, null);
        this.setPost(post);
        this.transferState.remove(POST_KEY);
      } else {
        this.fetchPostAndStoreKey(search, by);
      }
    }
  }

  /**
   * Busca una publicación según los criterios de búsqueda y la almacena en el estado de transferencia de la aplicación
   * entre el SSR y el cliente.
   * @param search - Criterios de búsqueda para recuperar una publicación. Podría ser un id o un slug,
   * dependiendo del parámetro `by`.
   * @param by - Criterios por los que buscar los posts.
   */
  fetchPostAndStoreKey(search, by): void {
    const video = this.detalleNoticiasService.getVideo(this.constantesGlobales.idAmbito, search, "post", by);
    if (video) {
      firstValueFrom(video).then((result) => {
        if (!result) {
          this.router.navigateByUrl("/404");
          return;
        }

        this.setPost(result);
        this.transferState.set(POST_KEY, result);
      });
    }
  }

  setPost(post) {
    if (!post) {
      this.router.navigateByUrl("/404");
    }
    this.post.set(post);
    if (post) {
      this.titleWrapperService.setTitle(post.tituloVideo);
    }
    this.updateMetaTags();
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante del post actual.
   * Genera un array de metaetiquetas que incluye datos como descripción, título, autor,
   * editor, y otros metadatos relacionados con el contenido del post.
   *
   * Las metaetiquetas generadas incluyen tanto etiquetas estándar como etiquetas específicas
   * para Open Graph (og) y Dublin Core (DC).
   *
   * @private
   */
  private updateMetaTags(): void {
    const metaTagsArray: MetaDefinition[] = [
      { name: "description", content: this.clearHTMLTags(this.post()?.descripcionVideo).slice(0, 150) },
      { property: "og:title", content: this.post()?.tituloVideo },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.logoMain}` },
      { property: "og:url", content: this.platformService.getLocationUrl() },
      { property: "og:description", content: this.clearHTMLTags(this.post()?.descripcionVideo).slice(0, 150) },
      { property: "article:author", content: this.post()?.nombre_autor },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: this.post()?.tituloVideo },
      { name: "DC.creator", content: this.post()?.nombre_autor },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: this.clearHTMLTags(this.post()?.descripcionVideo).slice(0, 150) },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: new Date().toISOString() },
    ];

    this.canonicalService.updateCanonicalURls(metaTagsArray);
  }

  getPublicidad(): void {
    const publi = this.publiService.getPubli();
    if (publi) {
      publi.pipe(first()).subscribe((result) => {
        if (result != null) {
          let publicidad_divisores_movil;
          result.imagenes.forEach((currentValue) => {
            if (currentValue.idPosicion === "34" || currentValue.idPosicion === "35" || currentValue.idPosicion === "36" || currentValue.idPosicion === "37" || currentValue.idPosicion === "38" || currentValue.idPosicion === "39" || currentValue.idPosicion === "40" || currentValue.idPosicion === "41" || currentValue.idPosicion === "42" || currentValue.idPosicion === "43" || currentValue.idPosicion === "44" || currentValue.idPosicion === "45" || currentValue.idPosicion === "46" || currentValue.idPosicion === "47" || currentValue.idPosicion === "48") {
              publicidad_divisores_movil[currentValue.nombrePosicion.replace("Movil - divisor fila ", "")] = currentValue;
            }
            return publicidad_divisores_movil;
          });
        }
      });
    }
  }

  clearHTMLTags(strToSanitize) {
    if (!strToSanitize || typeof strToSanitize !== "string") return "";
    return strToSanitize?.replace(/(<([^>]+)>)/gi, "");
  }
}
